/**
 * Guest Review Page - YUL ON TAP
 * 
 * Phase 2: Quick tap-to-review flow
 * Phase 3: Full i18n support (EN/FR)
 * Guest taps NFC → Lands here → Submits rating
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { engagementAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Star, Loader2, CheckCircle, Building2 } from 'lucide-react';

export default function GuestReview() {
  const { t, i18n } = useTranslation();
  const { hotelId, roomId } = useParams();
  const [searchParams] = useSearchParams();
  const tagId = searchParams.get('tag');
  // Allow language override via URL param (e.g., ?lang=fr)
  const langParam = searchParams.get('lang');

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    // Set language from URL if provided
    if (langParam && ['en', 'fr'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  useEffect(() => {
    fetchPageData();
  }, [hotelId, roomId]);

  const fetchPageData = async () => {
    try {
      const response = await engagementAPI.getReviewPageData(hotelId, roomId, tagId);
      setPageData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('guest.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(t('guest.review.selectRating'));
      return;
    }

    setSubmitting(true);
    try {
      await engagementAPI.submitReview(hotelId, roomId, { rating, comment }, tagId);
      setSubmitted(true);
      toast.success(t('guest.review.thankYou'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('guest.errors.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  // Rating labels based on current language
  const getRatingLabel = (r) => {
    const labels = {
      1: t('analytics.ratings.poor'),
      2: t('analytics.ratings.fair'),
      3: t('analytics.ratings.good'),
      4: t('analytics.ratings.veryGood'),
      5: t('analytics.ratings.excellent'),
    };
    return labels[r] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">{t('guest.review.thankYou')}</h2>
            <p className="text-gray-600">
              {t('guest.review.thankYouMessage')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
            <Building2 className="w-7 h-7 text-amber-600" />
          </div>
          <CardTitle className="text-xl">{pageData?.hotel?.name}</CardTitle>
          <CardDescription>
            {pageData?.room?.identifier ? `${t('table.room')} ${pageData.room.identifier}` : t('guest.review.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">{t('guest.review.question')}</p>
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  data-testid={`star-${star}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 ${
                      star <= (hoverRating || rating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-amber-600 mt-2 font-medium">
                {getRatingLabel(rating)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm text-gray-600">
              {t('guest.review.commentLabel')} ({t('common.optional')})
            </label>
            <Textarea
              data-testid="review-comment"
              placeholder={t('guest.review.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          <Button
            data-testid="submit-review-btn"
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('guest.review.submitting')}
              </>
            ) : (
              t('guest.review.submitReview')
            )}
          </Button>

          <p className="text-xs text-gray-400 text-center">
            {t('guest.review.feedbackHelps')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
